import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const CartCtx = createContext(null);
const KEY = "aperture_cart_v1";
const REFKEY = "aperture_ref";

const FORMAT_LABEL = {
  ebook: "eBook (PDF)",
  audio: "Audiobook",
  bundle: "eBook + Audiobook Bundle",
};

const normalizeFormat = (f) => (f || "ebook");

const dedupe = (arr) => {
  const seen = new Set();
  return arr.filter((i) => {
    const k = `${i.ebook_id}|${normalizeFormat(i.format)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return dedupe(JSON.parse(localStorage.getItem(KEY)) || []); } catch { return []; }
  });
  // Tracks whether we've hydrated from the server yet — prevents an empty
  // client cart from stomping on the server-saved cart on first login.
  const hydratedRef = useRef(false);
  const authTokenRef = useRef(null);
  const syncTimerRef = useRef(null);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  // Capture ?ref=CODE on any page load
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("ref");
    if (r) localStorage.setItem(REFKEY, r.toUpperCase());
  }, []);

  /**
   * add(ebook, format, unitPrice)
   *   ebook     — the ebook object
   *   format    — 'ebook' | 'audio' | 'bundle' (default 'ebook' for legacy calls)
   *   unitPrice — resolved price for that format; falls back to ebook.price
   */
  const add = (ebook, format = "ebook", unitPrice = null) => setItems((prev) => {
    const price = unitPrice != null ? unitPrice : ebook.price;
    // Same ebook in different formats = separate lines. Same ebook + same format = no dup.
    if (prev.find((i) => i.ebook_id === ebook.id && normalizeFormat(i.format) === format)) return prev;
    return [...prev, {
      ebook_id: ebook.id,
      title: ebook.title,
      format,
      format_label: FORMAT_LABEL[format] || format,
      price,
      cover: ebook.cover_image_url,
      quantity: 1,
    }];
  });
  // Normalize on BOTH sides — legacy items in localStorage have `format: undefined`
  // which broke the strict-equality check and made remove a no-op. This is the
  // root cause of the "remove button doesn't work" bug reported by the user.
  const remove = (ebook_id, format = "ebook") =>
    setItems((prev) => prev.filter(
      (i) => !(i.ebook_id === ebook_id && normalizeFormat(i.format) === format)
    ));
  const clear = () => setItems([]);
  const count = items.length;
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const referral = () => localStorage.getItem(REFKEY) || "";

  /**
   * Called by AuthContext right after a successful login/register. Merges the
   * current guest cart into the user's server-saved cart and re-hydrates local
   * state from the authoritative server response. Also flags hydratedRef so
   * subsequent add/remove will sync back to the server.
   */
  const mergeWithServer = async (token) => {
    authTokenRef.current = token || null;
    try {
      const r = await api.post("/cart/merge", { items });
      const merged = dedupe(r.data?.items || []);
      hydratedRef.current = true;
      setItems(merged);
    } catch {
      hydratedRef.current = true; // don't retry-loop
    }
  };

  /**
   * Called by AuthContext when the user has an existing session on page load
   * (page reload while logged in). Pulls the saved cart and merges it into
   * whatever is in localStorage.
   */
  const hydrateFromServer = async (token) => {
    authTokenRef.current = token || null;
    try {
      const r = await api.get("/cart");
      const server = dedupe(r.data?.items || []);
      const keys = new Set(items.map((i) => `${i.ebook_id}|${normalizeFormat(i.format)}`));
      const merged = [...items];
      for (const s of server) {
        const k = `${s.ebook_id}|${normalizeFormat(s.format)}`;
        if (!keys.has(k)) merged.push(s);
      }
      hydratedRef.current = true;
      setItems(merged);
    } catch {
      hydratedRef.current = true;
    }
  };

  const forgetSession = () => {
    authTokenRef.current = null;
    hydratedRef.current = false;
  };

  // Debounced server sync while logged in — every cart mutation posts the
  // current state to /api/cart. Guests are localStorage-only.
  useEffect(() => {
    if (!hydratedRef.current || !authTokenRef.current) return;
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      api.post("/cart", { items }).catch(() => {});
    }, 600);
    return () => clearTimeout(syncTimerRef.current);
  }, [items]);

  return (
    <CartCtx.Provider value={{
      items, add, remove, clear, count, subtotal, referral,
      mergeWithServer, hydrateFromServer, forgetSession,
    }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
